/**
 * AI Optimization fields (Type, Batch amount, Minimal %, Send until date)
 * Shown when aiOptimizationEnabled is checked.
 */

import type { FC } from 'react';

import { Input, SearchableSelect } from '@/components/ui';
import { DateTimePicker } from '@/components/ui/form';
import { scheduleLocalToUtcIso, utcIsoToScheduleLocal } from '@/features/smswiser-campaigns/utils/scheduleTimezone';

export interface AiOptimizationFieldsProps {
  type: 'ROI' | 'CTR' | '';
  setType: (v: 'ROI' | 'CTR' | '') => void;
  batchAmount: number | '';
  setBatchAmount: (v: number | '') => void;
  minimalValuePercent: number | '';
  setMinimalValuePercent: (v: number | '') => void;
  sendUntil: string;
  setSendUntil: (v: string) => void;
  isBusy?: boolean;
  compact?: boolean;
}

export const AiOptimizationFields: FC<AiOptimizationFieldsProps> = ({
  type,
  setType,
  batchAmount,
  setBatchAmount,
  minimalValuePercent,
  setMinimalValuePercent,
  sendUntil,
  setSendUntil,
  isBusy = false,
  compact = false,
}) => {
  const inputClass = compact ? 'h-7 min-w-0 text-xs' : 'h-9';
  const labelClass = compact ? 'mb-0.5 block text-[10px] text-muted-foreground' : 'mb-1 block text-xs text-muted-foreground';

  const fieldClass = 'flex min-w-0 flex-col';
  const wrapperClass = compact
    ? 'col-span-full ml-5 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2'
    : 'ml-6 grid grid-cols-1 gap-3 sm:grid-cols-2';

  return (
    <div className={wrapperClass} data-testid="ai-optimization-fields">
      <div className={fieldClass}>
        <label className={labelClass}>Type (ROI or CTR)</label>
        <SearchableSelect<'ROI' | 'CTR' | ''>
          options={[
            { value: '', label: 'Select...' },
            { value: 'ROI', label: 'ROI' },
            { value: 'CTR', label: 'CTR' },
          ]}
          value={type || ''}
          onChange={(v) => setType(v === 'ROI' || v === 'CTR' ? v : '')}
          placeholder="Select..."
          disabled={isBusy}
          className={compact ? 'h-7 text-xs' : ''}
        />
      </div>
      <div className={fieldClass}>
        <label className={labelClass}>Batch amount</label>
        <Input
          type="number"
          min={1}
          value={batchAmount}
          onChange={(e) => setBatchAmount(e.target.value ? Number(e.target.value) : '')}
          disabled={isBusy}
          className={inputClass}
        />
      </div>
      <div className={fieldClass}>
        <label className={labelClass}>Minimal value (%)</label>
        <Input
          type="number"
          min={0}
          max={100}
          step={0.01}
          value={minimalValuePercent}
          onChange={(e) => setMinimalValuePercent(e.target.value ? Number(e.target.value) : '')}
          disabled={isBusy}
          className={inputClass}
        />
      </div>
      <div className={fieldClass}>
        <label className={labelClass}>Send new batches until</label>
        <DateTimePicker
          value={sendUntil ? utcIsoToScheduleLocal(sendUntil) : ''}
          onChange={(v) => setSendUntil(v ? scheduleLocalToUtcIso(v) : '')}
          placeholder="dd/mm/yyyy, --:--"
          disabled={isBusy}
          minDateToday
          ariaLabel="Send until date and time"
          compact={compact}
          className={compact ? 'h-7 text-xs' : ''}
        />
      </div>
    </div>
  );
};
